import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import {
  GeneralCountryDto,
  GeneralReferenceDataApiApiV1ReferenceDataGeneralCurrenciesGetRequest,
} from "@/core/api/generated/v1";

export type CountryAutocompleteOption = BaseAutocompleteOption<GeneralCountryDto>;

export const entityToOption = (data: GeneralCountryDto): CountryAutocompleteOption => ({
  id: data.id || data.alpha2Code || "",
  title: StringHelper.join([data.name, `(${data.alpha2Code})`], " ", {
    skipEmpty: true,
  }),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface CountryAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<GeneralCountryDto> {
  searchFilters?: Partial<GeneralReferenceDataApiApiV1ReferenceDataGeneralCurrenciesGetRequest>;
}

export default function CountryAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: CountryAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={entityToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.generalReferenceDataApi.apiV1ReferenceDataGeneralCountriesGet,
        parameters: {},
        combineParameters: (params, newParams) => ({
          ...params,
          ...newParams,
        }),
        deps: [searchFilters],
      }}
      label='Country'
      placeholder='Search for country...'
      renderOption={(props, option) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='country' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                </>
              }
              secondary={
                <>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option.data?.alpha2Code}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
